import { useCallback, useEffect, useRef, useState } from "react";
import type { CompanyOrderUsecase } from "../../../../domain/usecases/company/company-order-usecase";
import type { Order } from "./types";
import { mapApiOrderToView } from "./utils";

export const ORDER_REFRESH_INTERVAL_MS = 5000;

/** Keep the current filter and loaded pages when refreshing. Stale responses cannot replace a newer view. */
export function useOrderList(
  usecase: CompanyOrderUsecase,
  companyId: number,
  status: string | undefined,
  keyword: string,
  dateFrom: string | undefined,
  dateTo: string | undefined,
  enabled: boolean,
) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  const session = useRef(0);
  const busy = useRef(false);
  const pages = useRef(1);
  const previousEnabled = useRef(enabled);
  const invalidateSession = useCallback(() => { session.current++; }, []);
  const pageSize = 20;
  const valid = Number.isFinite(companyId) && companyId > 0 && (!dateFrom || !dateTo || dateFrom <= dateTo);

  const refreshOrders = useCallback(async () => {
    if (!valid || busy.current) return;
    const version = session.current;
    busy.current = true;
    setIsRefreshing(true);
    try {
      const rows: Order[] = [];
      let totalPages = 0;
      const loadedPages = pages.current;
      for (let page = 0; page < loadedPages; page++) {
        const result = await usecase.getOrders(companyId, status, keyword, page, pageSize, dateFrom, dateTo);
        if (version !== session.current) return;
        totalPages = result.totalPages;
        rows.push(...result.items.map(mapApiOrderToView));
        if (page + 1 >= totalPages) break;
      }
      if (version !== session.current) return;
      setOrders(Array.from(new Map(rows.map(order => [order.id, order])).values()));
      pages.current = Math.min(loadedPages, Math.max(1, totalPages));
      setHasMore(pages.current < totalPages);
      setRefreshError("");
    } catch (error) {
      if (version === session.current) setRefreshError(error instanceof Error ? error.message : "주문을 갱신하지 못했습니다.");
    } finally {
      if (version === session.current) {
        busy.current = false;
        setIsRefreshing(false);
      }
    }
  }, [usecase, companyId, status, keyword, dateFrom, dateTo, valid]);

  const loadMoreOrders = useCallback(async () => {
    if (!valid || busy.current || !hasMore) return;
    const version = session.current;
    busy.current = true;
    setIsLoadingMore(true);
    try {
      const result = await usecase.getOrders(companyId, status, keyword, pages.current, pageSize, dateFrom, dateTo);
      if (version !== session.current) return;
      const mapped = result.items.map(mapApiOrderToView);
      setOrders(previous => Array.from(new Map([...previous, ...mapped].map(order => [order.id, order])).values()));
      pages.current++;
      setHasMore(pages.current < result.totalPages);
      setRefreshError("");
    } catch (error) {
      if (version === session.current) setRefreshError(error instanceof Error ? error.message : "추가 주문을 불러오지 못했습니다.");
    } finally {
      if (version === session.current) {
        busy.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [usecase, companyId, status, keyword, dateFrom, dateTo, valid, hasMore]);

  useEffect(() => {
    session.current++;
    busy.current = false;
    pages.current = 1;
    setOrders([]);
    setHasMore(false);
    setIsLoadingMore(false);
    setIsRefreshing(false);
    setRefreshError("");
    void refreshOrders();
    return invalidateSession;
  }, [refreshOrders, invalidateSession]);

  // Do not let an in-flight background refresh replace a detail/edit view.
  useEffect(() => {
    if (previousEnabled.current === enabled) return;
    previousEnabled.current = enabled;
    session.current++;
    busy.current = false;
    setIsRefreshing(false);
    setIsLoadingMore(false);
    if (enabled) void refreshOrders();
  }, [enabled, refreshOrders]);

  // 새 주문 안내는 우측 상단 알림(NotificationBell)이 맡는다. 여기서는 목록만 주기적으로 갱신한다.
  useEffect(() => {
    if (!enabled || !valid) return;
    const tick = () => {
      if (document.visibilityState === "hidden") return;
      void refreshOrders();
    };
    const timer = window.setInterval(tick, ORDER_REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
    };
  }, [enabled, valid, refreshOrders]);

  return { orders, hasMore, isLoadingMore, isRefreshing, refreshError, refreshOrders, loadMoreOrders };
}

